import { Button, Form, Grid, Popup, Select, Text } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useInvalidateGetWallets, useUpdateWalletsVisibility } from 'shared/data/wallets/v1';
import { useCompanyBasedOnUrl } from 'shared/hooks';
import { useWalletAvailableCurrencies } from 'shared/hooks/(wallets)';
import { z } from 'zod';

import { CurrencyMenuItem } from '../CurrencyMenuItem/CurrencyMenuItem';
import { filterAvailableCurrencies } from './AddCurrenciesPopupContent.helpers';
import { messages } from './AddCurrenciesPopupContent.messages';
import { schema } from './AddCurrenciesPopupContent.schema';
import { AddCurrenciesPopupContentProps } from './AddCurrenciesPopupContent.types';

export const AddCurrenciesPopupContent: FC<AddCurrenciesPopupContentProps> = (props) => {
    const { onClosePopup } = props;

    const { id: companyId } = useCompanyBasedOnUrl();

    const form = useForm<z.infer<typeof schema>>({
        defaultValues: {
            currencies: [],
        },
        resolver: zodResolver(schema),
    });

    const { currencies, isLoading } = useWalletAvailableCurrencies({
        needBalance: false,
        walletFilter: filterAvailableCurrencies,
    });

    const invalidateGetWallets = useInvalidateGetWallets();

    const { mutate: updateWalletsVisibility, isLoading: isLoadingUpdateWalletsVisibility } = useUpdateWalletsVisibility(
        {
            onSuccess: () => {
                invalidateGetWallets({
                    path: {
                        organizationId: companyId,
                    },
                });

                onClosePopup();
            },
        }
    );

    const handleSubmit = form.handleSubmit((values) => {
        updateWalletsVisibility({
            params: {
                path: {
                    organizationId: companyId,
                },
            },
            body: values.currencies,
        });
    });

    const selectedCurrencies = form.watch('currencies');

    const hasSelectedCurrencies = selectedCurrencies.length > 0;

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button
                        color='blue80'
                        size='medium'
                        type='submit'
                        progress={isLoadingUpdateWalletsVisibility}
                        disabled={!hasSelectedCurrencies}
                    >
                        {messages.add}
                    </Button>
                }
            />

            <Popup.Body>
                <Grid rowGap={16}>
                    <Text font='body' fontSize='medium'>
                        {messages.description}
                    </Text>

                    <Select
                        name='currencies'
                        label={messages.currency}
                        placeholder={messages.currencyPlaceholder}
                        multiple
                        items={currencies ?? []}
                        progress={isLoading}
                        customMenuItem={(item) => <CurrencyMenuItem {...item} subName={item.currencyName} />}
                        autocomplete
                        tagColor='blue80'
                        itemIdKey='walletId'
                        itemNameKey='id'
                    />
                </Grid>
            </Popup.Body>
        </Form>
    );
};
